import { Controller } from '@hotwired/stimulus';

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Overlays share one lock so closing the innermost does not hand scrolling back to the page while
// an outer one is still open.
const SCROLL_LOCKS = 'overlayScrollLocks';
const SCROLL_OVERFLOW = 'overlayPreviousOverflow';

// Open overlays, innermost last. Each one listens on the document, so without this an Escape
// meant for a dialog would also close the modal beneath it.
const openOverlays = [];

export const overlayStack = {
  push(overlay) {
    if (!openOverlays.includes(overlay)) openOverlays.push(overlay);
  },
  remove(overlay) {
    const index = openOverlays.indexOf(overlay);
    if (index !== -1) openOverlays.splice(index, 1);
  },
  isTop(overlay) {
    return openOverlays[openOverlays.length - 1] === overlay;
  }
};

// What every modal overlay (modal, dialog, sheet) does the same way: open from a trigger, lock
// the page scroll, keep Tab inside the panel, close on Escape, and give focus back on close.
//
// A subclass says which element is the panel (`panel`), how a trigger is found (`triggerSelector`)
// and what dismissing means (`dismiss`, which may animate before calling `finishClose`).
export default class OverlayBase extends Controller {
  // The element that carries `hidden`; the controller's own element unless the overlay is a
  // part of it (a navbar whose bar stays visible around a hidden menu).
  get surface() {
    return this.element;
  }

  connect() {
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    if (this.triggerSelector) document.addEventListener('click', this.handleTriggerClick);

    // An overlay can arrive already open (`open: true`), e.g. re-rendered by a turbo_stream after
    // a failed form submission. Acquire the same state open() would have set up.
    if (!this.surface.hidden) {
      this.previousFocus = document.activeElement;
      this.acquireOpenState();
    }
  }

  disconnect() {
    document.removeEventListener('click', this.handleTriggerClick);
    this.releaseOpenState();
  }

  open() {
    if (!this.surface.hidden) return;

    this.previousFocus = document.activeElement;
    this.surface.hidden = false;
    this.acquireOpenState();

    this.dispatch('open');
  }

  finishClose() {
    this.surface.hidden = true;
    this.releaseOpenState();

    if (this.previousFocus && this.previousFocus !== document.body &&
        typeof this.previousFocus.focus === 'function') {
      this.previousFocus.focus();
    }

    this.dispatch('close');
  }

  backdropClick(event) {
    if (event.target === this.element) this.dismiss();
  }

  // Delegated from document, so a trigger re-rendered by Turbo keeps working.
  handleTriggerClick(event) {
    const selector = this.triggerSelector;
    if (!selector) return;

    const trigger = event.target.closest(selector);
    if (!trigger) return;

    event.preventDefault();
    this.open();
  }

  handleKeydown(event) {
    if (!overlayStack.isTop(this)) return;

    if (event.key === 'Escape') {
      this.dismiss();
      return;
    }

    if (event.key === 'Tab') this.trapFocus(event);
  }

  trapFocus(event) {
    if (!this.panel) return;

    // Rendered elements only: a disabled or hidden one cannot take focus, and picking it as the
    // cycle's edge would let Tab escape the overlay.
    const focusable = [...this.panel.querySelectorAll(FOCUSABLE_SELECTOR)]
      .filter((element) => element.getClientRects().length > 0);
    if (focusable.length === 0) {
      event.preventDefault();
      this.panel.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    // Focus rests on the panel itself on open, or on the body after a backdrop click; either
    // way Shift+Tab would leave the overlay, so it enters the cycle instead.
    const outside = active === this.panel || !this.panel.contains(active);

    if (outside || (event.shiftKey && active === first) || (!event.shiftKey && active === last)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    }
  }

  acquireOpenState() {
    this.lockScroll();
    overlayStack.push(this);
    document.addEventListener('keydown', this.handleKeydown);

    requestAnimationFrame(() => {
      if (this.panel) this.panel.focus();
    });
  }

  releaseOpenState() {
    overlayStack.remove(this);
    document.removeEventListener('keydown', this.handleKeydown);
    this.unlockScroll();
  }

  lockScroll() {
    if (this.scrollLocked) return;

    const { body } = document;
    const locks = Number(body.dataset[SCROLL_LOCKS] || 0);
    if (locks === 0) {
      body.dataset[SCROLL_OVERFLOW] = body.style.overflow;
      body.style.overflow = 'hidden';
    }
    body.dataset[SCROLL_LOCKS] = String(locks + 1);
    this.scrollLocked = true;
  }

  unlockScroll() {
    if (!this.scrollLocked) return;

    const { body } = document;
    const locks = Math.max(Number(body.dataset[SCROLL_LOCKS] || 1) - 1, 0);
    this.scrollLocked = false;
    if (locks > 0) {
      body.dataset[SCROLL_LOCKS] = String(locks);
      return;
    }

    body.style.overflow = body.dataset[SCROLL_OVERFLOW] || '';
    delete body.dataset[SCROLL_LOCKS];
    delete body.dataset[SCROLL_OVERFLOW];
  }
}
